import type { MailMessage } from "../lib/api.ts";
import {
  formatMailAddressListFull,
  formatMailAddressListShort,
  formatMailContactFull,
  mailContactEmail,
  mailContactEmails,
  formatMailContactShort,
} from "./formatContact.ts";

type AddressField = "to" | "cc" | "bcc";

/** Lift To/Cc/Bcc from common provider aliases (Gmail/Graph wrappers). */
export function pickAddressField(
  message: MailMessage & Record<string, unknown>,
  primary: AddressField,
): unknown {
  if (primary === "to") {
    return (
      message.to ??
      message.toRecipients ??
      message.To ??
      message.toList ??
      message.recipients
    );
  }
  if (primary === "cc") {
    return (
      message.cc ??
      message.ccRecipients ??
      message.Cc ??
      message.ccList ??
      message.carbonCopy ??
      message.CarbonCopy
    );
  }
  return (
    message.bcc ??
    message.bccRecipients ??
    message.Bcc ??
    message.bccList ??
    message.blindCarbonCopy ??
    message.BlindCarbonCopy
  );
}

function pairDisplayWithEmails(
  names: string[] | string | undefined,
  emails: string[] | undefined,
): string {
  const nameList = Array.isArray(names)
    ? names.map(formatMailContactShort).filter(Boolean)
    : names
      ? [formatMailContactShort(names)].filter(Boolean)
      : [];
  const emailList = Array.isArray(emails) ? emails.filter(Boolean) : [];
  if (!nameList.length && !emailList.length) return "";
  const count = Math.max(nameList.length, emailList.length);
  const parts: string[] = [];
  for (let i = 0; i < count; i++) {
    const name = nameList[i] || "";
    const email = (emailList[i] || mailContactEmail(nameList[i]) || "").trim();
    if (name && email) {
      if (name.toLowerCase() === email.toLowerCase() || name.includes(`<${email}>`)) {
        parts.push(name.includes("@") ? name : `${name} <${email}>`);
      } else {
        parts.push(`${name} <${email}>`);
      }
    } else {
      parts.push(name || email);
    }
  }
  return parts.filter(Boolean).join(", ");
}

export function messageRecipientLineFull(message: MailMessage): string {
  return (
    pairDisplayWithEmails(message.to, message.toEmails) ||
    formatMailAddressListFull(message.to) ||
    formatMailContactFull(message.to)
  );
}

export function messageCcLineShort(message: MailMessage): string {
  return formatMailAddressListShort(message.cc);
}

export function messageBccLineShort(message: MailMessage): string {
  return formatMailAddressListShort(message.bcc);
}

export function messageCcLineFull(message: MailMessage): string {
  return (
    pairDisplayWithEmails(message.cc, message.ccEmails) ||
    formatMailAddressListFull(message.cc) ||
    formatMailContactFull(message.cc)
  );
}

export function messageBccLineFull(message: MailMessage): string {
  return (
    pairDisplayWithEmails(message.bcc, message.bccEmails) ||
    formatMailAddressListFull(message.bcc) ||
    formatMailContactFull(message.bcc)
  );
}

/** Expanded details: always show Cc (value or None). */
export function mailDetailCcDisplay(message: MailMessage): string {
  const line = messageCcLineFull(message);
  return line || "None";
}

/** Expanded details: Bcc row only on the sender's Sent copy. */
export function mailDetailBccDisplay(message: MailMessage, showSentCopy: boolean): string | null {
  if (!showSentCopy) return null;
  const line = messageBccLineFull(message);
  return line || "None";
}

export function coerceMailAddressLists(message: MailMessage): {
  cc?: string[] | string;
  bcc?: string[] | string;
  ccEmails?: string[];
  bccEmails?: string[];
} {
  const raw = message as MailMessage & Record<string, unknown>;
  const ccRaw = pickAddressField(raw, "cc");
  const bccRaw = pickAddressField(raw, "bcc");
  const ccEmails = message.ccEmails?.length ? message.ccEmails : mailContactEmails(ccRaw);
  const bccEmails = message.bccEmails?.length ? message.bccEmails : mailContactEmails(bccRaw);
  const ccList = Array.isArray(ccRaw)
    ? ccRaw.map(formatMailContactFull).filter(Boolean)
    : formatMailAddressListFull(ccRaw);
  const bccList = Array.isArray(bccRaw)
    ? bccRaw.map(formatMailContactFull).filter(Boolean)
    : formatMailAddressListFull(bccRaw);
  return {
    cc: ccList || undefined,
    bcc: bccList || undefined,
    ccEmails: ccEmails.length ? ccEmails : undefined,
    bccEmails: bccEmails.length ? bccEmails : undefined,
  };
}

export function messageIsSentCopy(
  message: MailMessage,
  folderSent: boolean,
  accountEmail?: string,
): boolean {
  if (message.sent) return true;
  if (folderSent) return true;
  const labels = message.labelIds ?? [];
  if (labels.includes("SENT")) return true;
  if (accountEmail && message.fromEmail && message.fromEmail.toLowerCase() === accountEmail.toLowerCase()) {
    return folderSent;
  }
  return false;
}
